import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { IFxHistoricalArchiveYear, IFxMovement } from '@modeso/types__aumico-lib-annualreport-be';
import { I18NextService } from 'angular-i18next';
import moment, { Moment } from 'moment';
import { IFxHistoricalEditRequest } from '../fx-conversion-table/fx-conversion-table.component';
import { FxTranslationHelper } from '../../../utils/FxTranslationHelper';

/** Matches `.custom-datepicker-wrapper` in the SCSS — used to place the flyout. */
const CALENDAR_WIDTH = 300;
const CALENDAR_HEIGHT = 340;

/** What the modal hands back — only the year it actually edited. */
export interface IFxHistoricalSavePayload {
    fliPath: string;
    accountKey: string;
    year: 'currentYear' | 'previousYear';
    previousYearRate?: number;
    /** The account's prior-year functional balance — together with the rate it forms the anchor. */
    functionalOpening: number;
    currentYearMovements?: IFxMovement[];
}

/** One closed year as shown in the history. */
export interface IFxHistoryYear {
    entry: IFxHistoricalArchiveYear;
    /**
     * Null when the chain carries no stored opening anchor — chains that began before the anchor
     * existed. The movements are still shown; the balances are simply not knowable, and inventing
     * them by counting backwards from today would assume every past year was declared correctly.
     */
    balances: {
        openingFunctional: number;
        openingDisplay: number;
        openingRate: number | null;
        closingFunctional: number;
        closingDisplay: number;
        closingRate: number | null;
    } | null;
}

/** One editable movement row. The display amount is derived, never saved. */
interface IEditableMovement {
    id: string;
    description: string;
    /** ISO date at UTC midnight, or '' — this is what gets saved. */
    date: string;
    /** What the user sees and types, formatted for the active locale. */
    dateInput: string;
    functionalAmount: number | null;
    rate: number | null;
    displayAmount: number | null;
}

/**
 * The editor for an account on `FxRateMethod.HISTORICAL`. Two scopes, because the two
 * years are different decisions:
 *
 * - `previousYear` — one rate for the position, nothing else. This year's movements would only be
 *   noise when the user is stating what the position was worth last year.
 * - `currentYear` — the whole chain: prior-year balance and rate (read-only context, edited in the
 *   other scope), the movements that explain the change, the closing balance and the weighted rate
 *   the position effectively translated at.
 *
 * Only rates are ever emitted. A rate derived from a typed display amount keeps FULL precision, so
 * the typed amount round-trips exactly; the 5-decimal form is display only.
 */
@Component({
    selector: 'aumico-fx-historical-modal',
    templateUrl: './fx-historical-modal.component.html',
    styleUrls: ['./fx-historical-modal.component.scss'],
})
export class FxHistoricalModalComponent implements OnInit {
    readonly rateDecimalPlaces = FxTranslationHelper.RATE_DECIMAL_PLACES;

    @Input() request!: IFxHistoricalEditRequest;
    @Input() functionalCurrencyName = '';
    @Input() displayCurrencyName = '';

    @Output() saveClicked = new EventEmitter<IFxHistoricalSavePayload>();
    @Output() cancelClicked = new EventEmitter<void>();

    movements: IEditableMovement[] = [];
    previousYearRate: number | null = null;
    previousYearDisplay: number | null = null;

    /** Closed years are collapsed by default in the current-year scope; the prior year leads with
        its own year and only reveals the rest on request. */
    historyExpanded = false;

    /** Index of the row whose calendar is open, or null. */
    dateFlyoutIndex: number | null = null;
    flyoutPosition: { top: number; left: number; right: number } = { top: 0, left: 0, right: 0 };
    /**
     * Material's DateAdapter in this app is the MomentDateAdapter (provided by
     * aumico-annualreport.module), so mat-calendar speaks Moment — not Date.
     */
    calendarSelected: Moment | null = null;

    title = '';
    saveLabel = '';
    cancelLabel = '';
    openingLabel = '';
    closingLabel = '';
    deltaLabel = '';
    explainedLabel = '';
    descriptionLabel = '';
    dateLabel = '';
    datePlaceholderLabel = '';
    functionalLabel = '';
    rateLabel = '';
    displayLabel = '';
    weightedLabel = '';
    movementsLabel = '';
    historyLabel = '';
    historyMoreLabel = '';
    historyLessLabel = '';
    historyCountLabel = '';
    yearLabel = '';
    historyOpeningLabel = '';
    historyClosingLabel = '';
    addLabel = '';
    deleteLabel = '';
    rateHintLabel = '';
    previousRequiredLabel = '';
    lockedNoteLabel = '';

    constructor(private readonly i18next: I18NextService) {}

    ngOnInit(): void {
        this.resolveTexts();
        this.previousYearRate = this.request.previousYearRate;
        this.previousYearDisplay = this.deriveDisplay(this.request.functionalOpening, this.previousYearRate);
        if (this.isPreviousYear) {
            return;
        }
        this.movements = (this.request.historical?.currentYearMovements ?? []).map((movement) => ({
            id: movement.id,
            description: movement.description ?? '',
            date: movement.date ?? '',
            dateInput: movement.date == null ? '' : this.formatDate(new Date(movement.date)),
            functionalAmount: movement.functionalAmount,
            rate: movement.rate,
            displayAmount: this.deriveDisplay(movement.functionalAmount, movement.rate),
        }));
    }

    // #region archive (read-only history)

    /** True once anything was ever carried forward — i.e. from the second year onwards. */
    get hasHistory(): boolean {
        return this.historyYears.length > 0;
    }

    /**
     * Every closed year with the balances it opened and closed at, so the history reconciles.
     *
     * It is walked FORWARD from `archiveOpening` — the anchor the first FX report stored — applying
     * each year's movements in turn. Deriving it backwards from today's balance instead would
     * assume every past year was declared completely and correctly; one wrong entry back then would
     * silently shift every figure before it. Newest first, the way the user reads it.
     */
    get historyYears(): IFxHistoryYear[] {
        const entries = this.request.archive.filter((entry) => (entry.movements?.length ?? 0) > 0);
        const opening = this.request.historical?.archiveOpening;
        let functional = opening?.functionalAmount ?? 0;
        let display = opening == null ? 0 : this.round2(opening.functionalAmount * opening.rate);

        const years: IFxHistoryYear[] = [];
        for (const entry of entries) {
            const movements = entry.movements ?? [];
            const openingFunctional = functional;
            const openingDisplay = display;
            functional = this.round2(functional + movements.reduce((sum, m) => sum + (m.functionalAmount ?? 0), 0));
            display = this.round2(display + movements.reduce((sum, m) => sum + this.displayOf(m), 0));

            years.push({
                entry,
                balances:
                    opening == null
                        ? null
                        : {
                              openingFunctional,
                              openingDisplay,
                              openingRate: openingFunctional === 0 ? null : openingDisplay / openingFunctional,
                              closingFunctional: functional,
                              closingDisplay: display,
                              closingRate: functional === 0 ? null : display / functional,
                          },
            });
        }

        return years.reverse();
    }

    /**
     * What the user sees without asking. The prior-year scope leads with the year it is about —
     * that is the one explaining the balance being rated. The current-year scope keeps the history
     * out of the way entirely, because there the working area is the movement entry above.
     */
    get visibleHistoryYears(): IFxHistoryYear[] {
        if (this.historyExpanded) {
            return this.historyYears;
        }

        return this.isPreviousYear ? this.historyYears.slice(0, 1) : [];
    }

    /** Nothing to toggle when the leading year is already everything there is. */
    get showHistoryToggle(): boolean {
        return !this.isPreviousYear || this.historyYears.length > 1;
    }

    toggleHistory(): void {
        this.historyExpanded = !this.historyExpanded;
    }

    displayOf(movement: IFxMovement): number {
        return this.round2((movement.functionalAmount ?? 0) * (movement.rate ?? 0));
    }

    formatArchiveYear(year: IFxHistoryYear): string {
        return year.entry.balanceSheetDate == null ? '' : this.formatDate(new Date(year.entry.balanceSheetDate));
    }

    trackByYear(index: number, year: IFxHistoryYear): string {
        return `${year.entry.balanceSheetDate ?? index}`;
    }

    // #endregion

    // #region derived figures

    get isPreviousYear(): boolean {
        return this.request.year === 'previousYear';
    }

    /** The movements build on the prior year's value, so nothing below works without it. */
    get isPreviousYearMissing(): boolean {
        return this.previousYearRate == null;
    }

    /**
     * The rate is only the account's own when that year is on HISTORICAL, and only this scope
     * edits it. A locked prior year is genuinely locked — the unlock sits above the table, the same
     * one-way action that governs the period rates.
     */
    get isPreviousYearEditable(): boolean {
        return this.isPreviousYear && this.request.previousYearHistorical && !this.request.previousYearLocked;
    }

    /** Locked, so the fields are read-only — say why rather than leaving the user guessing. */
    get showLockedNote(): boolean {
        return this.isPreviousYear && this.request.previousYearHistorical && this.request.previousYearLocked;
    }

    get delta(): number {
        return this.round2(this.request.functionalClosing - this.request.functionalOpening);
    }

    get declared(): number {
        return this.round2(this.movements.reduce((sum, m) => sum + (m.functionalAmount ?? 0), 0));
    }

    get unexplained(): number {
        return this.round2(this.delta - this.declared);
    }

    /** Opening plus every declared movement — the account's closing value in display currency. */
    get closingDisplay(): number | null {
        if (this.previousYearDisplay == null) {
            return null;
        }

        return this.round2(
            this.signedLike(this.request.functionalOpening, this.previousYearDisplay) +
                this.movements.reduce((sum, m) => sum + (this.deriveDisplay(m.functionalAmount, m.rate) ?? 0), 0),
        );
    }

    /** What the position effectively translated at, once all movements are in. */
    get weightedRate(): number | null {
        const closing = this.closingDisplay;

        return closing == null || this.request.functionalClosing === 0
            ? null
            : closing / this.request.functionalClosing;
    }

    // #endregion

    /** Complete = it carries a description, an amount and a rate. The amount/rate pair fills itself. */
    isMovementComplete(movement: IEditableMovement): boolean {
        return movement.description.trim() !== '' && movement.functionalAmount != null && movement.rate != null;
    }

    /**
     * Saving stays blocked while the prior year is unset or any row is half-typed — neither is a
     * decision the user can mean. An unexplained remainder is fine: the position stays open.
     */
    get isSaveDisabled(): boolean {
        if (this.isPreviousYear) {
            return !this.isPreviousYearEditable || this.previousYearRate == null;
        }

        return this.movements.some((movement) => !this.isMovementComplete(movement));
    }

    // #region previous year

    onPreviousYearRateChange(value: number | null): void {
        this.previousYearRate = value;
        this.previousYearDisplay = this.deriveDisplay(this.request.functionalOpening, value);
    }

    onPreviousYearDisplayChange(value: number | null): void {
        this.previousYearDisplay = value;
        this.previousYearRate = this.deriveRate(this.request.functionalOpening, value);
    }

    /** Once typed, the amount shows the sign it counts with — the functional balance's. */
    onPreviousYearDisplayBlur(): void {
        this.previousYearDisplay = this.signedLike(this.request.functionalOpening, this.previousYearDisplay);
    }

    // #endregion

    // #region movement editing

    onAddMovement(): void {
        this.movements = [
            ...this.movements,
            {
                id: FxHistoricalModalComponent.generateId(),
                description: '',
                date: '',
                dateInput: '',
                // Pre-fill the outstanding amount: the common case is one movement explaining it all.
                functionalAmount: this.unexplained !== 0 ? this.unexplained : null,
                rate: null,
                displayAmount: null,
            },
        ];
    }

    onRemoveMovement(index: number): void {
        this.movements = this.movements.filter((_, i) => i !== index);
    }

    onDescriptionChange(index: number, value: string): void {
        this.movements[index].description = value;
    }

    /** The rate stays authoritative; the display amount follows it. */
    onFunctionalChange(index: number, value: number | null): void {
        const movement = this.movements[index];
        movement.functionalAmount = value;
        movement.displayAmount = this.deriveDisplay(value, movement.rate);
    }

    onRateChange(index: number, value: number | null): void {
        const movement = this.movements[index];
        movement.rate = value;
        movement.displayAmount = this.deriveDisplay(movement.functionalAmount, value);
    }

    /** The other direction: a typed amount derives the rate at full precision. */
    onDisplayChange(index: number, value: number | null): void {
        const movement = this.movements[index];
        movement.displayAmount = value;
        movement.rate = this.deriveRate(movement.functionalAmount, value);
    }

    /** Once typed, the amount shows the sign it counts with — the movement's. */
    onDisplayBlur(index: number): void {
        const movement = this.movements[index];
        movement.displayAmount = this.signedLike(movement.functionalAmount, movement.displayAmount);
    }

    // #endregion

    // #region date entry — the new-project wizard's pattern (typed string OR calendar)

    /** Raw keystrokes; only normalized on blur so typing "3112" mid-way isn't fought. */
    onDateInput(index: number, value: string): void {
        this.movements[index].dateInput = value;
    }

    /**
     * Parses what the user typed. Separators are interchangeable (`.`, `/`, `-`, space) and a
     * plain digit run works too — "31122025" is a date. Anything unparseable is cleared rather
     * than silently kept, since the field is optional.
     */
    onDateInputBlur(index: number): void {
        const movement = this.movements[index];
        const parsed = this.parseDate(movement.dateInput);
        if (parsed == null) {
            movement.date = '';
            movement.dateInput = '';

            return;
        }
        movement.date = parsed.toISOString();
        movement.dateInput = this.formatDate(parsed);
    }

    /**
     * Enter confirms the typed date and closes the calendar. The event must not reach the document:
     * ham-modal listens there and would treat it as "save the whole modal".
     */
    onDateEnter(event: Event, index: number): void {
        event.preventDefault();
        event.stopPropagation();
        this.onDateInputBlur(index);
        this.closeDateFlyout();
    }

    /** Escape dismisses only the calendar — without this it would reach ham-modal and close it. */
    onDateEscape(event: Event): void {
        if (this.dateFlyoutIndex != null) {
            event.preventDefault();
            event.stopPropagation();
            this.closeDateFlyout();
        }
    }

    onCalendarSelect(index: number, selected: Moment | Date | null): void {
        if (selected != null) {
            // Accept either shape — the adapter decides, and the wizard hits the same fork.
            const year = selected instanceof Date ? selected.getFullYear() : selected.year();
            const month = selected instanceof Date ? selected.getMonth() : selected.month();
            const day = selected instanceof Date ? selected.getDate() : selected.date();
            // Rebuild at UTC midnight so the calendar day never shifts across time zones.
            const utc = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
            this.movements[index].date = utc.toISOString();
            this.movements[index].dateInput = this.formatDate(utc);
            this.calendarSelected = moment.utc(utc).startOf('day');
        }
        this.closeDateFlyout();
    }

    openDateFlyout(input: HTMLInputElement, index: number): void {
        const rect = input.getBoundingClientRect();
        // Flip above the field when there is not enough room below for the calendar.
        const openUpwards = rect.bottom + CALENDAR_HEIGHT > window.innerHeight;
        this.flyoutPosition = {
            top: openUpwards ? Math.max(8, rect.top - CALENDAR_HEIGHT - 4) : rect.bottom + 4,
            left: rect.left,
            right: window.innerWidth - rect.left - CALENDAR_WIDTH,
        };
        this.calendarSelected =
            this.movements[index].date === '' ? null : moment.utc(this.movements[index].date).startOf('day');
        this.dateFlyoutIndex = index;
    }

    closeDateFlyout(): void {
        this.dateFlyoutIndex = null;
    }

    /**
     * Close the calendar on any click outside it. hamFlyout's own backdrop cannot do this here:
     * it sits at z-index 99 while ham-modal is at 1100, so clicks inside the modal never reach it.
     */
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (this.dateFlyoutIndex == null) {
            return;
        }
        const target = event.target as HTMLElement | null;
        if (target == null) {
            return;
        }
        // The calendar itself and the field that owns it are not "outside".
        if (target.closest('.custom-datepicker-wrapper') != null || target.closest('.fx-hist-date-field') != null) {
            return;
        }
        this.closeDateFlyout();
    }

    // #endregion

    onSave(): void {
        if (this.isPreviousYear) {
            this.saveClicked.emit({
                fliPath: this.request.fliPath,
                accountKey: this.request.accountKey,
                year: 'previousYear',
                previousYearRate: this.previousYearRate ?? undefined,
                functionalOpening: this.request.functionalOpening,
            });

            return;
        }
        // Belt and braces: `isSaveDisabled` already blocks half-typed rows from getting here.
        const movements: IFxMovement[] = this.movements
            .filter((m) => this.isMovementComplete(m))
            .map((m) => ({
                id: m.id,
                description: m.description.trim() === '' ? undefined : m.description.trim(),
                date: m.date.trim() === '' ? undefined : m.date.trim(),
                functionalAmount: m.functionalAmount as number,
                rate: m.rate as number,
            }));
        this.saveClicked.emit({
            fliPath: this.request.fliPath,
            accountKey: this.request.accountKey,
            year: 'currentYear',
            functionalOpening: this.request.functionalOpening,
            currentYearMovements: movements,
        });
    }

    onCancel(): void {
        this.cancelClicked.emit();
    }

    trackById(_index: number, movement: IEditableMovement): string {
        return movement.id;
    }

    private deriveDisplay(functionalAmount: number | null, rate: number | null): number | null {
        return functionalAmount == null || rate == null ? null : this.round2(functionalAmount * rate);
    }

    /**
     * Full precision on purpose — rounding here would shift the amount the user just typed. A rate
     * has no sign: amounts are typed as plain figures, and the functional amount decides whether
     * the display amount is an inflow or an outflow. Dividing signed figures would turn a positive
     * figure typed against an outflow into a negative rate — and the outflow into an inflow.
     */
    private deriveRate(functionalAmount: number | null, displayAmount: number | null): number | null {
        return functionalAmount == null || displayAmount == null || functionalAmount === 0
            ? null
            : Math.abs(displayAmount) / Math.abs(functionalAmount);
    }

    /** The display amount with the functional amount's sign — the sign it is translated with. */
    private signedLike(functionalAmount: number | null, displayAmount: number | null): number | null {
        if (displayAmount == null || functionalAmount == null || functionalAmount === 0) {
            return displayAmount;
        }

        return functionalAmount < 0 ? -Math.abs(displayAmount) : Math.abs(displayAmount);
    }

    private round2(value: number): number {
        return FxTranslationHelper.round2(value);
    }

    /** `dd.MM.yyyy`, or `MM/dd/yyyy` on English — same rule as the wizard. */
    private formatDate(date: Date): string {
        if (isNaN(date.getTime())) {
            return '';
        }
        const year = date.getUTCFullYear();
        const month = `${date.getUTCMonth() + 1}`.padStart(2, '0');
        const day = `${date.getUTCDate()}`.padStart(2, '0');

        return this.isEnglishLocale ? `${month}/${day}/${year}` : `${day}.${month}.${year}`;
    }

    private parseDate(value: string): Date | null {
        const trimmed = value.trim();
        if (trimmed === '') {
            return null;
        }
        let day: number;
        let month: number;
        let year: number;

        const digitsOnly = trimmed.replace(/\D/g, '');
        if (/^\d+$/.test(trimmed) && (digitsOnly.length === 8 || digitsOnly.length === 6)) {
            // "31122025" / "311225" — a bare digit run, in the locale's field order.
            const yearDigits = digitsOnly.length === 8 ? 4 : 2;
            const first = parseInt(digitsOnly.slice(0, 2), 10);
            const second = parseInt(digitsOnly.slice(2, 4), 10);
            year = parseInt(digitsOnly.slice(4, 4 + yearDigits), 10);
            [day, month] = this.isEnglishLocale ? [second, first] : [first, second];
        } else {
            const parts = trimmed.split(/[.\-/\s]+/).filter((part) => part !== '');
            if (parts.length !== 3) {
                return null;
            }
            const [first, second, third] = parts.map((part) => parseInt(part, 10));
            [day, month] = this.isEnglishLocale ? [second, first] : [first, second];
            year = third;
        }

        if (year < 100) {
            year += 2000;
        }
        if (
            isNaN(year) ||
            isNaN(month) ||
            isNaN(day) ||
            year < 1900 ||
            year > 2100 ||
            month < 1 ||
            month > 12 ||
            day < 1 ||
            day > 31
        ) {
            return null;
        }
        const parsed = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));

        // Rejects impossible days such as 31.02 — the Date constructor would roll them over.
        return parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === day ? parsed : null;
    }

    private get isEnglishLocale(): boolean {
        return (this.i18next.language ?? '').startsWith('en');
    }

    private static generateId(): string {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }

        return `fxm-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    }

    private resolveTexts(): void {
        const t = (key: string): string => this.i18next.t(key) as string;
        this.title = this.isPreviousYear
            ? t('aumico_annual_report_fx_hist_modal_title_previous')
            : t('aumico_annual_report_fx_hist_modal_title');
        this.saveLabel = t('aumico_annual_report_fx_hist_modal_save');
        this.cancelLabel = t('aumico_annual_report_fx_hist_modal_cancel');
        this.openingLabel = t('aumico_annual_report_fx_hist_modal_opening');
        this.closingLabel = t('aumico_annual_report_fx_hist_modal_closing');
        this.deltaLabel = t('aumico_annual_report_fx_hist_modal_delta');
        this.explainedLabel = t('aumico_annual_report_fx_hist_modal_explained');
        this.descriptionLabel = t('aumico_annual_report_fx_hist_modal_description');
        this.dateLabel = t('aumico_annual_report_fx_hist_modal_date');
        this.datePlaceholderLabel = t('aumico_annual_report_fx_hist_modal_date_placeholder');
        this.functionalLabel = t('aumico_annual_report_fx_hist_modal_functional');
        // "Kurs", not the table's "Kurstyp": this field holds the rate itself, not its kind.
        this.rateLabel = t('aumico_annual_report_fx_hist_modal_rate');
        this.displayLabel = t('aumico_annual_report_fx_hist_modal_display');
        this.weightedLabel = t('aumico_annual_report_fx_hist_modal_weighted');
        this.movementsLabel = t('aumico_annual_report_fx_hist_modal_movements');
        this.historyLabel = t('aumico_annual_report_fx_hist_modal_history');
        this.historyMoreLabel = t('aumico_annual_report_fx_hist_modal_history_more');
        this.historyLessLabel = t('aumico_annual_report_fx_hist_modal_history_less');
        const closedYears = this.historyYears.length;
        this.historyCountLabel = this.i18next.t(
            closedYears === 1
                ? 'aumico_annual_report_fx_hist_modal_history_count_one'
                : 'aumico_annual_report_fx_hist_modal_history_count_other',
            { count: closedYears },
        ) as string;
        this.yearLabel = t('aumico_annual_report_fx_hist_modal_year');
        this.historyOpeningLabel = t('aumico_annual_report_fx_hist_modal_history_opening');
        this.historyClosingLabel = t('aumico_annual_report_fx_hist_modal_history_closing');
        this.addLabel = t('aumico_annual_report_fx_table_hist_add_movement');
        this.deleteLabel = t('aumico_annual_report_fx_hist_modal_delete');
        this.rateHintLabel = t('aumico_annual_report_fx_hist_modal_rate_hint');
        this.previousRequiredLabel = t('aumico_annual_report_fx_hist_modal_previous_required');
        this.lockedNoteLabel = t('aumico_annual_report_fx_hist_modal_locked_note');
    }
}
